/**
 * Route Path Constants
 * Centralized path definitions for type-safe navigation
 */

export const PATHS = {
  // Auth
  LOGIN: '/login',
  REGISTER: '/register',
  
  // Onboarding
  ONBOARDING_WELCOME: '/onboarding/welcome',
  ONBOARDING_COMPLETE: '/onboarding/complete',
  REGISTER_SUCCESS: '/register/success',
  
  // Nurse Onboarding
  NURSE_ONBOARDING_STEP: (step: number) => `/nurse/onboarding/step/${step}`,
  NURSE_ONBOARDING_COMPLETE: '/nurse/onboarding/complete',
  
  // Main
  HOME: '/',
  DASHBOARD: '/dashboard',
  RECEPTIONIST_DASHBOARD: '/receptionist/dashboard',
  
  // Settings
  SETTINGS: '/settings',
  RECEPTIONIST_SETTINGS: '/receptionist/settings',
  
  // Appointments Management (Global)
  APPOINTMENTS: '/appointments',
  APPOINTMENTS_DASHBOARD: '/appointments/dashboard',
  APPOINTMENTS_LIST: '/appointments/list',
  APPOINTMENTS_TODAY: '/appointments/today',
  APPOINTMENTS_CALENDAR: '/appointments/calendar',
  APPOINTMENTS_NEW: '/appointments/new',
  APPOINTMENT_DETAIL: (id: string) => `/appointments/${id}`,
  APPOINTMENT_EDIT: (id: string) => `/appointments/${id}/edit`,
  
  // Receptionist
  RECEPTIONIST_APPOINTMENTS: '/receptionist/appointments',
  RECEPTIONIST_APPOINTMENTS_NEW: '/receptionist/appointments/new',
  RECEPTIONIST_APPOINTMENTS_TODAY: '/receptionist/appointments/today',
  RECEPTIONIST_APPOINTMENTS_CALENDAR: '/receptionist/appointments/calendar',
  RECEPTIONIST_APPOINTMENTS_LIST: '/receptionist/appointments/list',
  RECEPTIONIST_APPOINTMENT_DETAIL: (id: string) => `/receptionist/appointments/${id}`,
  RECEPTIONIST_PATIENTS: '/receptionist/patients',
  RECEPTIONIST_PATIENTS_LIST: '/receptionist/patients/list',
  RECEPTIONIST_PATIENT_NEW: '/receptionist/patients/new',
  RECEPTIONIST_PATIENT_DETAIL: (id: string) => `/receptionist/patients/${id}`,
  RECEPTIONIST_DOCTORS: '/receptionist/doctors',
  RECEPTIONIST_DOCTORS_LIST: '/receptionist/doctors/list',
  
  // Patients
  PATIENTS: '/patients',
  PATIENT_DETAIL: (id: string) => `/patients/${id}`,
  PATIENT_HEALTH_RECORDS: (id: string) => `/patients/${id}/health-records`,
  
  // Doctor Profile
  DOCTOR_PROFILE: '/doctor/profile',
  DOCTOR_APPOINTMENT_PROCESS: (id: string) => `/doctor/schedule/${id}/process`,
  
  // Referrals Management
  REFERRALS: '/referrals',
  REFERRAL_NEW: '/referrals/new',
  REFERRAL_EDIT: (id: string) => `/referrals/${id}/edit`,
  REFERRAL_DETAIL: (id: string) => `/referrals/${id}`,

  // Service Requests
  SERVICE_REQUESTS: '/service-requests',
  SERVICE_REQUEST_DETAIL: (id: string) => `/service-requests/${id}`,
  SERVICE_REQUEST_NEW: '/service-requests/new',
  
  // Agency Management (PHASE 0)
  AGENCY_REGISTER: '/agency/register',
  AGENCY_DASHBOARD: '/agency/dashboard',
  AGENCY_PROFILE: '/agency/profile',
  AGENCY_SETTINGS: '/agency/settings',
  
  // Agency Onboarding (Multi-Step Profile Setup)
  AGENCY_ONBOARDING: '/agency/onboarding',
  AGENCY_ONBOARDING_STEP: (step: number) => `/agency/onboarding/step/${step}`,
  AGENCY_ONBOARDING_COMPLETE: '/agency/onboarding/complete',
  
  // Role Selection
  ROLE_SELECT: '/role-select',
  
  // Legal
  TERMS_OF_SERVICE: '/terms-of-service',
  TERMS_OF_USE: '/terms-of-use',
  PRIVACY_POLICY: '/privacy-policy',

  // Legal (external URLs - open in new tab)
  LEGAL_TERMS_OF_USE_URL: 'https://ubora-health.com/terms-of-use',
  LEGAL_PRIVACY_POLICY_URL: 'https://ubora-health.com/privacy-policy',

  // Testing
  THEME_TEST: '/theme-test',
  
  // Error Pages
  NOT_FOUND: '/404',
  FORBIDDEN: '/403',
  SERVER_ERROR: '/500',

  // Reviews
  REVIEWS: '/reviews',

  // Caregiver Management
  CAREGIVERS: '/caregivers',
  CAREGIVERS_LIST: '/caregivers/list',
  CAREGIVER_NEW: '/caregivers/new',
  CAREGIVER_DETAIL: (id: string) => `/caregivers/${id}`,
  CAREGIVER_EDIT: (id: string) => `/caregivers/${id}/edit`,

  // Staffing board (availability + schedule)
  STAFFING: '/staffing',

  // Caregiver Onboarding (7-Step USA Healthcare Format)
  CAREGIVER_ONBOARDING: '/caregiver/onboarding',
  CAREGIVER_ONBOARDING_START: (id: string) => `/caregiver/onboarding/${id}`,
  CAREGIVER_ONBOARDING_STEP: (id: string, step: number) => `/caregiver/onboarding/${id}/step/${step}`,
  CAREGIVER_ONBOARDING_COMPLETE: (id: string) => `/caregiver/onboarding/${id}/complete`,

  // Support Tickets
  TICKETS: '/tickets',
  TICKETS_NEW: '/tickets/new',
  TICKET_DETAIL: (id: string) => `/tickets/${id}`,

  // Forced password reset
  RESET_PASSWORD: '/reset-password',
  FORGOT_PASSWORD: '/forgot-password',
  VERIFY_EMAIL: '/verify-email',
} as const;

export type PathKey = keyof typeof PATHS;
